import { Controller } from "@hotwired/stimulus";

// Re-opens the loyalty modal on page load when the URL
// carries ?loyalty=open. Mirrors
// restaurant_item_auto_open_controller: the login
// round-trip and the post-enroll redirect both land back
// on the venue page with the param, and this re-opens the
// modal so the flow continues without a second tap.
//
// Two flows use this:
//
// 1. Login round-trip: a guest opens the modal (which
//    sets ?loyalty=open), taps a login provider, auths
//    via Stytch (which preserves query params), and lands
//    back here: the modal re-opens in the "Join" state.
//
// 2. Post-enroll: "Join now" redirects to the venue page
//    with ?loyalty=open, and the modal re-opens showing
//    the points balance.
//
// Wiring (on the loyalty quick-link button, alongside
// the modal-toggle controller):
//   data-controller="modal-toggle loyalty-modal-auto-open"
//   data-loyalty-modal-auto-open-modal-id-value="{dom_id}"
//   data-action="click->modal-toggle#requestOpen
//     click->loyalty-modal-auto-open#setQueryParam
//     modalClosed@window->loyalty-modal-auto-open#clearQueryParam"
export default class extends Controller {
  static values = { modalId: String };

  connect() {
    const url = new URL(window.location);
    if (url.searchParams.get("loyalty") !== "open") return;

    // Strip so a later refresh doesn't reopen the modal.
    url.searchParams.delete("loyalty");
    history.replaceState({}, "", url);

    // Defer so the modal controller has bound its
    // requestModalOpen@window listener by the time we
    // dispatch (both controllers connect on the same
    // page load).
    setTimeout(() => {
      this.dispatch("requestModalOpen", {
        detail: { id: this.modalIdValue, displayBackdrop: true },
        prefix: false,
      });
    }, 50);
  }

  // Called when the customer opens the modal, so the
  // Stytch OAuth redirect (authUrl + pathname + search)
  // carries the param through the login round-trip.
  setQueryParam() {
    const url = new URL(window.location);
    url.searchParams.set("loyalty", "open");
    history.replaceState({}, "", url);
  }

  // Called when a modal closes, so a plain open/close of
  // the loyalty modal leaves a clean URL behind. The
  // modalClosed event fires for every modal in the app, so
  // guard on the closing modal's id, or closing an
  // unrelated modal would strip the param while the loyalty
  // modal is still open (and about to carry it into login).
  clearQueryParam(event) {
    if (event?.detail?.id !== this.modalIdValue) return;

    const url = new URL(window.location);
    url.searchParams.delete("loyalty");
    history.replaceState({}, "", url);
  }
}
